import React, { useState, useEffect } from 'react';
import './schoology-integration.css';

/**
 * Schoology LMS Integration Component
 * 
 * This component provides UI for:
 * 1. Connecting to Schoology LMS
 * 2. Viewing connected courses
 * 3. Deploying Morpheus tutors to Schoology courses
 * 4. Managing institutional settings
 */
const SchoologyIntegration = ({ user }) => {
  // States for Schoology integration
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [courses, setCourses] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [institutionalMode, setInstitutionalMode] = useState(false);
  const [tokenName, setTokenName] = useState('Tokens');
  const [schoologyUrl, setSchoologyUrl] = useState('https://app.schoology.com');

  // Check connection status on load
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Check if connected to Schoology
  const checkConnectionStatus = async () => {
    setConnectionStatus('Checking connection...');
    
    try {
      const response = await fetch('/api/schoology/status');
      const data = await response.json();
      
      setIsConnected(data.connected);
      setConnectionStatus(data.connected ? 'Connected to Schoology' : 'Not connected to Schoology');
      
      if (data.connected) {
        fetchCourses();
        fetchModels();
      }
    } catch (error) {
      console.error('Error checking Schoology connection:', error);
      setConnectionStatus('Error checking connection');
      setIsConnected(false);
    }
  };

  // Connect to Schoology with API credentials
  const connectToSchoology = async () => {
    if (!consumerKey || !consumerSecret) {
      setConnectionStatus('Consumer Key and Secret are required');
      return;
    }
    
    setConnectionStatus('Connecting to Schoology...');
    
    try {
      const response = await fetch('/api/schoology/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          consumerKey,
          consumerSecret,
          apiUrl: schoologyUrl,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsConnected(true);
        setConnectionStatus('Successfully connected to Schoology');
        fetchCourses();
        fetchModels();
      } else {
        setConnectionStatus(`Failed to connect: ${data.error}`);
      }
    } catch (error) {
      console.error('Error connecting to Schoology:', error);
      setConnectionStatus('Error connecting to Schoology');
    }
  };

  // Fetch courses from Schoology
  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/schoology/courses');
      const data = await response.json();
      
      if (data.success) {
        setCourses(data.courses);
      } else {
        console.error('Failed to fetch courses:', data.error);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch available models
  const fetchModels = async () => {
    try {
      const response = await fetch('/api/models/list');
      const data = await response.json();
      
      if (data.success) {
        setModels(data.models);
      } else {
        console.error('Failed to fetch models:', data.error);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  // Deploy model to Schoology course
  const deployModel = async () => {
    if (!selectedCourseId) {
      setDeploymentResult({ success: false, message: 'Please select a course' });
      return;
    }
    
    if (!selectedModelId) {
      setDeploymentResult({ success: false, message: 'Please select a model' });
      return;
    }
    
    setIsDeploying(true);
    setDeploymentResult(null);
    
    try {
      const response = await fetch('/api/schoology/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courseId: selectedCourseId,
          modelId: selectedModelId,
          institutionalMode,
          tokenName,
        }),
      });
      
      const data = await response.json();
      
      setDeploymentResult({
        success: data.success,
        message: data.success 
          ? `Successfully deployed ${selectedModelId} to course ${selectedCourseId}` 
          : `Deployment failed: ${data.error}`,
      });
    } catch (error) {
      console.error('Error deploying model:', error);
      setDeploymentResult({
        success: false,
        message: 'Error deploying model',
      });
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <div className="schoology-integration">
      <h2>Schoology LMS Integration</h2>
      
      <div className="connection-status">
        <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </div>
        <p>{connectionStatus}</p>
      </div>
      
      {!isConnected && (
        <div className="connection-form">
          <h3>Connect to Schoology</h3>
          
          <div className="connection-methods">
            <div className="api-key-method">
              <h4>Connect with API Credentials</h4>
              <div className="form-group">
                <label htmlFor="schoologyUrl">Schoology API URL:</label>
                <input
                  type="text"
                  id="schoologyUrl"
                  value={schoologyUrl}
                  onChange={(e) => setSchoologyUrl(e.target.value)}
                  placeholder="https://api.schoology.com/v1"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="consumerKey">Consumer Key:</label>
                <input
                  type="password"
                  id="consumerKey"
                  value={consumerKey}
                  onChange={(e) => setConsumerKey(e.target.value)}
                  placeholder="Enter your Schoology Consumer Key"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="consumerSecret">Consumer Secret:</label>
                <input
                  type="password"
                  id="consumerSecret"
                  value={consumerSecret}
                  onChange={(e) => setConsumerSecret(e.target.value)}
                  placeholder="Enter your Schoology Consumer Secret"
                />
              </div>
              
              <button 
                className="connect-button"
                onClick={connectToSchoology}
                disabled={!consumerKey || !consumerSecret}
              >
                Connect with API Credentials
              </button>
            </div>
            
            <div className="schoology-note">
              <h4>Note About Schoology API</h4>
              <p>
                To use this integration, you need Schoology Enterprise with API access enabled.
                Contact your Schoology administrator to get your Consumer Key and Secret.
              </p>
              <p>
                The Schoology integration supports OAuth 1.0a authentication and LTI 1.3 for
                deeper integration with courses.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {isConnected && (
        <div className="integration-panel">
          <div className="courses-section">
            <h3>Your Schoology Courses</h3>
            
            {courses.length === 0 ? (
              <p>No courses found. Create a course in Schoology first.</p>
            ) : (
              <div className="course-list">
                <select
                  value={selectedCourseId}
                  onChange={(e) => setSelectedCourseId(e.target.value)}
                >
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="models-section">
            <h3>Available Models</h3>
            
            {models.length === 0 ? (
              <p>No models available. Train or import a model first.</p>
            ) : (
              <div className="model-list">
                <select
                  value={selectedModelId}
                  onChange={(e) => setSelectedModelId(e.target.value)}
                >
                  <option value="">Select a model</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.specialty})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="institutional-settings">
            <h3>Institutional Settings</h3>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={institutionalMode}
                  onChange={(e) => setInstitutionalMode(e.target.checked)}
                />
                Enable Institutional Mode
              </label>
            </div>
            
            {institutionalMode && (
              <div className="form-group">
                <label htmlFor="tokenName">Token Display Name:</label>
                <input
                  type="text"
                  id="tokenName"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="Points, Stars, etc."
                />
                <p className="token-info">
                  This name will be used for the grade column in Schoology's gradebook.
                </p>
              </div>
            )}
          </div>
          
          <div className="deployment-section">
            <button
              className="deploy-button"
              onClick={deployModel}
              disabled={!selectedCourseId || !selectedModelId || isDeploying}
            >
              {isDeploying ? 'Deploying...' : 'Deploy to Schoology'}
            </button>
            
            {deploymentResult && (
              <div className={`deployment-result ${deploymentResult.success ? 'success' : 'error'}`}>
                {deploymentResult.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoologyIntegration;